import { Component, Host, h, State, Prop } from "@stencil/core";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
import "dayjs/locale/nl-be";
dayjs.locale("nl-be");
import { sensorInfo } from "../../utils/sensorInfo";
import {
  ILiftStatusSensorDataResponse,
  TSeverity,
  IStatusMessage,
} from "../../utils/types";
import { isEmpty } from "lodash-es";
import { getStore, ILiftStatusState } from "../../store";
import statusCodesState from "../../stores/status-codes.store";
import { ObservableMap } from "@stencil/store";

@Component({
  tag: "ls-status-messages",
  styleUrl: "ls-status-messages.css",
  shadow: true,
})
export class LsStatusMessages {
  /**
   * Whether or not to show the title of the component.
   */
  @Prop() readonly showTitle = true;

  /**
   * Allows user to filter messages based on severity
   */
  // Leading comma is to allow for <empty_string>
  @Prop() readonly severityOptions = ",impact,informational,non-impact";

  /**
   * Used to store severity options after parsing
   */
  private severityFilter: TSeverity[];

  /**
   * The data received from the subscription is placed in this state.
   * The component is automatically rerendered after the state changes.
   */
  @State() statusMessages: IStatusMessage[] = [];

  /**
   * The timestamp of when the sensor last changed.
   */
  @State() lastChange = "";

  /**
   * The timer is used to update the relative timestamps.
   */
  @State() time: number = Date.now();

  /**
   * The data from the socket server. This data is saved so that the relative timestamp can be updated.
   */
  private sensorData: ILiftStatusSensorDataResponse;

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  private store: ObservableMap<ILiftStatusState>;

  /**
   * The sensor ID associated with the status messages.
   */
  private readonly sensorId = 513;

  /**
   * The title of the component.
   */
  private componentTitle: string;

  /**
   * Keeps track of the setInterval ID.
   */
  private timer: number;

  // This function processes the raw data from the socket server
  private updateData = (sensorData: ILiftStatusSensorDataResponse) => {
    if (isEmpty(sensorData?.values[this.sensorId])) return;

    const items = sensorData.values[this.sensorId].split(",");

    let result: IStatusMessage[] = [];

    for (const item of items) {
      const parts = item.split(":");
      const identifier = parts[0];
      const timestamp = dayjs.unix(parseInt(parts[1], 10) / 1000);
      const humanReadableTimestamp = timestamp.format("D MMM YYYY H:mm:ss");
      const relativeTimestamp = timestamp.fromNow();
      const active = parseInt(parts[2], 10);

      // The description and severity level are retrieved from the central store
      const description =
        statusCodesState[identifier]?.description ?? identifier;
      const severity = statusCodesState[identifier]?.level ?? null;

      // Add the item to the results array
      if (active === 1 && this.severityFilter?.includes(severity)) {
        result.push({
          id: identifier,
          humanReadableTimestamp,
          relativeTimestamp,
          description,
          severity,
        });
      }
    }

    this.lastChange = dayjs(sensorData.updated[this.sensorId]).fromNow();
    this.statusMessages = result;
    this.sensorData = sensorData;
  };

  private isSeverity = (value: string): value is TSeverity => {
    return ["", "impact", "informational", "non-impact"].includes(
      value as TSeverity
    );
  };

  connectedCallback() {
    // The timer is used to update the relative timestamps
    this.timer = window.setInterval(() => {
      // Update the data, so that the relative timestamp gets updated
      this.updateData(this.sensorData);
      // The timestamp is actually not displayed, but is only used to update the state and rerender the DOM
      this.time = Date.now();
    }, 20000);

    const severityFilterSplit = this.severityOptions
      .replace(/\s+/g, "")
      .split(",");
    const severityFilter: TSeverity[] = [];

    severityFilterSplit.forEach((item) => {
      if (this.isSeverity(item) && !severityFilter.includes(item)) {
        severityFilter.push(item);
      }
    });

    if (severityFilter.length > 0) {
      this.severityFilter = severityFilter;
    } else {
      console.group(
        "%cError%c [LsStatusMessages] Incorrect severity filter options supplied",
        "color: #DC2626; background-color: #FEF2F2; display: inline-block; padding: 0 4px; border-radius: 2px;",
        ""
      );
      console.log(
        "Allowed options: '<empty_string>, impact, informational and non-impact"
      );
      console.log(
        "Check https://springtree.github.io/ednl-liftstatus-web-components/#/components/ls-status-messages for more information"
      );
      console.groupEnd();
    }
  }

  async componentWillLoad() {
    this.store = await getStore(this.idKey);

    this.updateData(this.store.state.currentSensorData);

    this.store.onChange("currentSensorData", (sensorData) => {
      this.updateData(sensorData);
    });

    this.componentTitle = sensorInfo.getLabel(this.sensorId);
  }

  render() {
    let errorMessage: boolean | string = false;

    if (this.store.state.error.type === "Socket") {
      errorMessage = "Geen verbinding";
      clearInterval(this.timer);
      this.timer = null;
    } else if (this.store.state.error.type === "Token") {
      errorMessage = this.store.state.error.message;
      clearInterval(this.timer);
      this.timer = null;
    }

    return (
      <Host>
        <slot></slot>
        <div class="ls-status-messages">
          {this.showTitle && (
            <div class="title">{this.componentTitle || "Statusmeldingen"}</div>
          )}

          {/* If lastChange does not have a timestamp, we know the data is still loading or an error occurred */}
          {this.lastChange === "" ? (
            <div class="messages">
              {errorMessage === false ? (
                <div class="label">Meldingen laden...</div>
              ) : (
                <div class="label">{errorMessage}</div>
              )}
              {errorMessage && (
                <div class="timestamp">
                  More information in the browser console
                </div>
              )}
            </div>
          ) : (
            <div class="messages">
              {/* Check if there are any status messages */}
              {this.statusMessages.length > 0 ? (
                this.statusMessages.map((message) => (
                  <div class="message">
                    <div class="label" title={message.id}>
                      {message.description || message.id}
                    </div>
                    <div class={`severity ${message.severity}`}>
                      {message.severity || "geen"}
                    </div>
                    <div class="human-readable-timestamp">
                      {message.humanReadableTimestamp}
                    </div>
                    <div class="timestamp relative-timestamp">
                      {message.relativeTimestamp}
                    </div>
                  </div>
                ))
              ) : (
                <dt class="label">Geen actuele statusmeldingen</dt>
              )}

              {/* Hide the last changed text when there are status messages */}
              {this.statusMessages.length === 0 && (
                <div class="timestamp">
                  Laatste wijziging: {this.lastChange}
                </div>
              )}
            </div>
          )}
        </div>
      </Host>
    );
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    this.timer = null;
  }
}
